import { ReactElement, useEffect, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { ContentLabelField } from '@hopskipdrive/react-components';
import * as Yup from 'yup';

import RiderDriverSearch from '../../../components/RiderDriverSearch';
import { IncidentIndividual } from '../../../types/incidents';

interface OffenderIdFieldProps {
  defaultValue?: number;
  currentOffenderType: string;
  riders: IncidentIndividual[];
  drivers: IncidentIndividual[];
  carePartnerDrivers?: IncidentIndividual[];
  riderAssistants?: IncidentIndividual[];
}

export const offenderIdValidation = Yup.number().required('Alleged offender is required').nullable();

export const OffenderIdField = ({
  defaultValue,
  currentOffenderType,
  riders,
  drivers,
  carePartnerDrivers,
  riderAssistants,
}: OffenderIdFieldProps): ReactElement => {
  const [options, setOptions] = useState<IncidentIndividual[]>([]);
  const previousOffenderType = useRef(currentOffenderType);
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name: 'offenderId',
    control,
    defaultValue,
  });
  const setOffendingUsers = (offenderType: string) => offenderdUsersMap[offenderType.toLowerCase()] || [];

  useEffect(() => {
    setOptions(setOffendingUsers(currentOffenderType?.toLowerCase()) || []);
  }, [currentOffenderType, drivers, riders]);

  useEffect(() => {
    if (previousOffenderType.current && previousOffenderType.current !== currentOffenderType) {
      previousOffenderType.current = currentOffenderType;
      field.onChange(null);
    }
  }, [currentOffenderType]);

  const offenderdUsersMap = {
    driver: drivers,
    carepartnerdriver: carePartnerDrivers,
    passenger: riders,
    riderassistant: riderAssistants,
  };

  return (
    <ContentLabelField
      id="offenderId"
      title={'Alleged Offending User'}
      editMode={true}
      required={true}
      editView={
        <RiderDriverSearch
          options={options}
          value={options.find((item) => item.id === field.value) || null}
          onChange={(selected) => {
            selected && !Array.isArray(selected) ? field.onChange(selected?.id) : field.onChange(null);
          }}
          error={!!fieldState.error}
          helperText={fieldState.error?.message || 'Only a single alleged offender can be added to each incident.'}
          placeholder={'Search or select User'}
          multiple={false}
        />
      }
    />
  );
};
